import { GraphQLFormattedError } from 'graphql';

interface GraphQLError {
  status: number;
  message: string;
  code: string;
}

interface NetworkError {
  code: string; // e.g. "AUTH_203". Custom error codes come from insta-agency-backend/.../AppError.ts
  message: string; // most messages come from insta-agency-backend/.../AppError.ts
  status: number; // HTTP status codes e.g. 401, 403, 500.
}

export const ERROR_CODE_INCORRECT_APP_CONTEXT = 'AUTH_203';

export const findErrorByCode = (
  error: any,
  errorCode: string,
): NetworkError | undefined => {
  const networkErrors =
    error?.networkError?.result?.errors || error?.result?.errors;

  const targetError = networkErrors?.find(
    (error: NetworkError) => error?.code === errorCode,
  );

  return targetError;
};

/**
 * Checks if an error message contains technical data that shouldn't be shown to users.
 * Technical indicators include:
 * - JSON structures ({}, [])
 * - Stack trace patterns (at, Error:, file paths)
 * - File paths and extensions
 * - Long hex/UUID patterns
 */
const containsTechnicalData = (message: string): boolean => {
  if (!message) return false;

  // Check for JSON structures, stack trace patterns, and other error patterns.
  if (
    message.includes('{') ||
    message.includes('}') ||
    message.includes('[') ||
    message.includes(']') ||
    message.includes('Error:') ||
    message.includes('TypeError:') ||
    message.includes('ReferenceError:') ||
    message.includes('[object ') ||
    message.includes('Object {') ||
    message.includes('Array [') ||
    (message.includes('undefined') && message.length > 100)
  ) {
    return true;
  }

  // Check for file paths
  if (/\.(ts|js|tsx|jsx|json|py|java|go|rs)(\s|$|:)/.test(message)) {
    return true;
  }

  // Check for long hex/UUID patterns
  if (
    /\b[0-9a-f]{32,}\b/i.test(message) ||
    /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/i.test(
      message,
    )
  ) {
    return true;
  }

  return false;
};

/**
 * Handles GraphQL errors from the graphql responses.
 *
 * If the error message is technical, it throws a default message.
 *
 * If the error message is user friendly, it throws the error message.
 *
 * Most of the error messages come from insta-agency-backend/src/helpers/AppError.ts.
 *
 * @param apolloClientErrors - Array of GraphQL errors from Apollo Client response.
 * @param defaultMessage - Optional default error message if error message is technical.
 * @throws Error with appropriate message based on error message.
 */
export const handleGraphQLErrors = (
  apolloClientErrors: readonly GraphQLFormattedError[] | undefined | null,
  defaultMessage: string = 'An unexpected server error occurred',
): void => {
  const errors = apolloClientErrors as GraphQLError[];

  if (apolloClientErrors && apolloClientErrors.length > 0) {
    const error = errors[0];

    const errorMessage = error.message || '';

    if (containsTechnicalData(errorMessage)) {
      throw new Error(defaultMessage);
    }

    throw new Error(errorMessage);
  }
};
